/**
 * Projection utility functions for isometric rendering
 * Handles skew, pitch, and face-specific transform calculations
 */
export default class ProjectionUtils {
  /**
   * Calculate pitch scale from skew angle
   * @param {number} skewAngle - Camera skew angle
   * @return {number} Pitch scale factor
   */
  static calculatePitchScale(skewAngle) {
    const pitchRad = (skewAngle * Math.PI) / 4;
    return Math.sin(pitchRad);
  }

  /**
   * Apply isometric transform to coordinates
   * @param {number} x - X coordinate
   * @param {number} z - Z coordinate
   * @param {number} angle - Rotation angle in degrees
   * @param {number} yScale - Y scale factor
   * @return {Object} Transformed coordinates {x, z}
   */
  static applyIsometricTransform(x, z, angle, yScale) {
    const rad = angle * Math.PI / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    return {
      x: x * cos - z * sin,
      z: (x * sin + z * cos) * yScale
    };
  }

  /**
   * Calculate screen bounds for a cuboid
   * Used for depth sorting and occlusion detection
   * @param {Object} cuboidData - Cuboid position and size data
   * @param {number} cosAngle - Cosine of camera angle
   * @param {number} sinAngle - Sine of camera angle
   * @param {number} yScale - Y scale factor
   * @param {number} cubeSize - Size of one cube unit
   * @return {Object} Screen bounds {minX, maxX, minY, maxY}
   */
  static calculateScreenBounds(cuboidData, cosAngle, sinAngle, yScale, cubeSize) {
    // O(1) analytic screen AABB — no corner objects or projections needed.
    // For linear projection screenX = wx*cos - wz*sin, screenY = (wx*sin + wz*cos)*yScale - wy
    // each extreme is the sum of per-axis extremes (independent monotonic terms).
    const x0 = cuboidData.x * cubeSize;
    const x1 = (cuboidData.x + cuboidData.width) * cubeSize;
    const y0 = cuboidData.y * cubeSize;
    const y1 = (cuboidData.y + cuboidData.height) * cubeSize;
    // worldZ = -gameZ * cubeSize
    const z0 = -(cuboidData.z + cuboidData.depth) * cubeSize;
    const z1 = -cuboidData.z * cubeSize;

    // screenX = wx*cos - wz*sin
    const sxA = x0 * cosAngle, sxB = x1 * cosAngle;
    const szA = z0 * sinAngle, szB = z1 * sinAngle;
    const sxMin = (sxA < sxB ? sxA : sxB) - (szA > szB ? szA : szB);
    const sxMax = (sxA > sxB ? sxA : sxB) - (szA < szB ? szA : szB);

    // screenY = (wx*sin + wz*cos)*yScale - wy
    const hxA = x0 * sinAngle, hxB = x1 * sinAngle;
    const hzA = z0 * cosAngle, hzB = z1 * cosAngle;
    const hSumLo = (hxA < hxB ? hxA : hxB) + (hzA < hzB ? hzA : hzB);
    const hSumHi = (hxA > hxB ? hxA : hxB) + (hzA > hzB ? hzA : hzB);
    const hMin = hSumLo * yScale, hMax = hSumHi * yScale;
    // yScale can be negative (viewing from below), so re-order
    const syMin = (hMin < hMax ? hMin : hMax) - y1;
    const syMax = (hMin > hMax ? hMin : hMax) - y0;

    return { minX: sxMin, maxX: sxMax, minY: syMin, maxY: syMax };
  }

  /**
   * O(1) analytic screen AABB for a position with pre-computed min/max bounds.
   * Same algorithm as calculateScreenBounds but takes minX/maxX/minY/maxY/minZ/maxZ.
   * @param {Object} pos - Position with minX/maxX/minY/maxY/minZ/maxZ
   * @param {number} cosAngle - Cosine of camera angle
   * @param {number} sinAngle - Sine of camera angle
   * @param {number} yScale - Y scale factor
   * @param {number} cubeSize - Size of one cube unit
   * @return {Object} Screen bounds {minX, maxX, minY, maxY}
   */
  static calculateScreenBoundsFromBounds(pos, cosAngle, sinAngle, yScale, cubeSize) {
    const x0 = pos.minX * cubeSize, x1 = pos.maxX * cubeSize;
    // Use visual Y bounds for screen envelope when available (sprites have larger
    // visual extent than their sorting AABB). SAT uses minY/maxY; screen overlap uses visual.
    const y0 = (pos.visualMinY !== undefined ? pos.visualMinY : pos.minY) * cubeSize;
    const y1 = (pos.visualMaxY !== undefined ? pos.visualMaxY : pos.maxY) * cubeSize;
    const z0 = -pos.maxZ * cubeSize, z1 = -pos.minZ * cubeSize;

    const sxA = x0 * cosAngle, sxB = x1 * cosAngle;
    const szA = z0 * sinAngle, szB = z1 * sinAngle;
    const sxMin = (sxA < sxB ? sxA : sxB) - (szA > szB ? szA : szB);
    const sxMax = (sxA > sxB ? sxA : sxB) - (szA < szB ? szA : szB);

    const hxA = x0 * sinAngle, hxB = x1 * sinAngle;
    const hzA = z0 * cosAngle, hzB = z1 * cosAngle;
    const hSumLo = (hxA < hxB ? hxA : hxB) + (hzA < hzB ? hzA : hzB);
    const hSumHi = (hxA > hxB ? hxA : hxB) + (hzA > hzB ? hzA : hzB);
    const hMin = hSumLo * yScale, hMax = hSumHi * yScale;
    const syMin = (hMin < hMax ? hMin : hMax) - y1;
    const syMax = (hMin > hMax ? hMin : hMax) - y0;

    return { minX: sxMin, maxX: sxMax, minY: syMin, maxY: syMax };
  }

  /**
   * Check if two screen bounds overlap
   * @param {Object} boundsA - First bounds {minX, maxX, minY, maxY}
   * @param {Object} boundsB - Second bounds {minX, maxX, minY, maxY}
   * @return {boolean} True if bounds overlap
   */
  static screenBoundsOverlap(boundsA, boundsB) {
    return !(
      boundsA.maxX < boundsB.minX ||
      boundsA.minX > boundsB.maxX ||
      boundsA.maxY < boundsB.minY ||
      boundsA.minY > boundsB.maxY
    );
  }

  /**
   * Calculate face transform for top/bottom faces
   * @param {string} face - 'top' or 'bottom'
   * @param {number} x - Face X position
   * @param {number} y - Face Y position
   * @param {number} yScale - Y scale factor
   * @param {number} angle - Camera angle in degrees
   * @return {string} CSS transform string
   */
  static calculateTopBottomFaceTransform(face, x, y, yScale, angle) {
    if (face === 'top') {
      return `translate(${x}px, ${y}px) scaleY(${yScale}) rotate(${angle}deg)`;
    } else {
      return `translate(${x}px, ${y}px) scaleY(${-yScale}) rotate(${-angle}deg)`;
    }
  }

  /**
   * Calculate face transform for side faces
   * @param {number} corner1X - First corner X
   * @param {number} corner1Y - First corner Y
   * @param {number} corner2X - Second corner X
   * @param {number} corner2Y - Second corner Y
   * @param {number} scale - Scale factor for the face
   * @return {string} CSS transform string
   */
  static calculateSideFaceTransform(corner1X, corner1Y, corner2X, corner2Y, scale) {
    const skewAngle = Math.atan2(corner2Y - corner1Y, corner2X - corner1X) * 180 / Math.PI;
    return `translate(${corner1X}px, ${corner1Y}px) skewY(${skewAngle}deg) scaleX(${scale})`;
  }

  /**
   * Determine if viewing from below based on skew angle
   * @param {number} skewAngle - Camera skew angle
   * @return {boolean} True if viewing from below
   */
  static isViewingFromBelow(skewAngle) {
    return skewAngle < 0;
  }

  /**
   * Calculate the angle between two points
   * @param {number} x1 - First point X
   * @param {number} y1 - First point Y
   * @param {number} x2 - Second point X
   * @param {number} y2 - Second point Y
   * @return {number} Angle in degrees
   */
  static angleBetweenPoints(x1, y1, x2, y2) {
    return Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;
  }

  /**
   * Check if cuboid's screen bounds are entirely outside the viewport (frustum).
   * Returns true if cuboid should be CULLED (not rendered).
   *
   * @param {Object} screenBounds - Cuboid's screen bounds {minX, maxX, minY, maxY}
   * @param {number} camScreenX - Camera position X in screen space
   * @param {number} camScreenY - Camera position Y in screen space
   * @param {number} halfWidth - Half viewport width (in pre-zoom pixel space)
   * @param {number} halfHeight - Half viewport height (in pre-zoom pixel space)
   * @param {number} margin - Optional buffer margin to prevent popping (default 0)
   * @return {boolean} True if cuboid should be culled
   */
  static isOutsideFrustum(screenBounds, camScreenX, camScreenY, halfWidth, halfHeight, margin = 0) {
    // Adjust bounds relative to camera position
    const relMinX = screenBounds.minX - camScreenX;
    const relMaxX = screenBounds.maxX - camScreenX;
    const relMinY = screenBounds.minY - camScreenY;
    const relMaxY = screenBounds.maxY - camScreenY;

    // AABB test: cuboid is outside if entirely beyond any edge
    return (
      relMaxX < -halfWidth - margin ||   // Entirely left of viewport
      relMinX > halfWidth + margin ||    // Entirely right of viewport
      relMaxY < -halfHeight - margin ||  // Entirely above viewport
      relMinY > halfHeight + margin      // Entirely below viewport
    );
  }

  /**
   * 2D cross product of vectors OA and OB
   * Positive if counter-clockwise, negative if clockwise, zero if collinear
   * @param {Object} o - Origin point {x, y}
   * @param {Object} a - First point {x, y}
   * @param {Object} b - Second point {x, y}
   * @return {number} Cross product value
   */
  static crossProduct2D(o, a, b) {
    return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  }

  /**
   * Graham scan algorithm to compute convex hull
   * @param {Array} points - Array of {x, y} points
   * @return {Array} Convex hull points in counter-clockwise order
   */
  static grahamScan(points) {
    if (points.length <= 3) return points.slice();

    // Find the lowest point (break ties by x)
    let pivotIdx = 0;
    for (let i = 1; i < points.length; i++) {
      if (points[i].y < points[pivotIdx].y ||
          (points[i].y === points[pivotIdx].y && points[i].x < points[pivotIdx].x)) {
        pivotIdx = i;
      }
    }

    // Copy points and swap pivot to position 0
    const pts = points.slice();
    [pts[0], pts[pivotIdx]] = [pts[pivotIdx], pts[0]];
    const p0 = pts[0];

    // Sort by polar angle with respect to pivot
    const sorted = pts.slice(1).sort((a, b) => {
      const cross = this.crossProduct2D(p0, a, b);
      if (Math.abs(cross) < 1e-10) {
        // Collinear: closer point comes first
        const distA = (a.x - p0.x) ** 2 + (a.y - p0.y) ** 2;
        const distB = (b.x - p0.x) ** 2 + (b.y - p0.y) ** 2;
        return distA - distB;
      }
      return -cross; // Negative because we want CCW order
    });

    // Build the hull
    const hull = [p0];
    for (const point of sorted) {
      while (hull.length > 1 && this.crossProduct2D(hull[hull.length - 2], hull[hull.length - 1], point) <= 0) {
        hull.pop();
      }
      hull.push(point);
    }

    return hull;
  }

  /**
   * Compute the convex hull of a projected cuboid
   * @param {Object} cuboid - Cuboid with minX/maxX/minY/maxY/minZ/maxZ
   * @param {number} cosAngle - Cosine of camera rotation
   * @param {number} sinAngle - Sine of camera rotation
   * @param {number} yScale - Y scale factor from skew
   * @param {number} cubeSize - Size of one cube unit
   * @return {Array} Array of {x, y} points forming the convex hull in CCW order
   */
  static computeProjectedConvexHull(cuboid, cosAngle, sinAngle, yScale, cubeSize) {
    // All 8 corners in game coordinates
    const corners = [
      { x: cuboid.minX, y: cuboid.minY, z: cuboid.minZ },
      { x: cuboid.maxX, y: cuboid.minY, z: cuboid.minZ },
      { x: cuboid.minX, y: cuboid.maxY, z: cuboid.minZ },
      { x: cuboid.maxX, y: cuboid.maxY, z: cuboid.minZ },
      { x: cuboid.minX, y: cuboid.minY, z: cuboid.maxZ },
      { x: cuboid.maxX, y: cuboid.minY, z: cuboid.maxZ },
      { x: cuboid.minX, y: cuboid.maxY, z: cuboid.maxZ },
      { x: cuboid.maxX, y: cuboid.maxY, z: cuboid.maxZ }
    ];

    // Project all corners to screen space
    const projected = corners.map(corner => {
      const worldX = corner.x * cubeSize;
      const worldZ = -corner.z * cubeSize;
      const worldY = corner.y * cubeSize;

      const screenX = worldX * cosAngle - worldZ * sinAngle;
      const screenY = (worldX * sinAngle + worldZ * cosAngle) * yScale - worldY;

      return { x: screenX, y: screenY };
    });

    return this.grahamScan(projected);
  }

  /**
   * Compute convex hull analytically for an axis-aligned box under isometric projection.
   * The silhouette of an AABB is always a hexagon (or degenerate quad) formed by
   * excluding the nearest and farthest corners along the view direction.
   * O(1) computation vs O(n log n) Graham Scan.
   *
   * @param {Object} cuboid - Cuboid with minX/maxX/minY/maxY/minZ/maxZ
   * @param {number} cosAngle - Cosine of camera rotation
   * @param {number} sinAngle - Sine of camera rotation
   * @param {number} yScale - Y scale factor from skew
   * @param {number} cubeSize - Size of one cube unit
   * @return {Array} Array of {x, y} points forming the convex hull in CCW order
   */
  // Pre-computed CCW winding orders for AABB silhouette hulls, indexed by near corner.
  // Corner indices: 0=(x0,y0,z0) 1=(x1,y0,z0) 2=(x0,y1,z0) 3=(x1,y1,z0)
  //                 4=(x0,y0,z1) 5=(x1,y0,z1) 6=(x0,y1,z1) 7=(x1,y1,z1)
  // Near corner = maximizes depth (wx*sin + wy*yScale + wz*cos).
  // Near corner index = (sin>=0?1:0) + (yScale>=0?2:0) + (cos>=0?4:0)
  // Each entry lists the 6 hull corner indices in CCW screen-space winding order.
  // Verified numerically via Graham scan for all 8 octants.
  static _HULL_WINDING = [
    [2, 6, 4, 5, 1, 3],  // near=0, far=7
    [3, 2, 0, 4, 5, 7],  // near=1, far=6
    [7, 6, 4, 0, 1, 3],  // near=2, far=5
    [6, 2, 0, 1, 5, 7],  // near=3, far=4
    [6, 7, 5, 1, 0, 2],  // near=4, far=3
    [7, 3, 1, 0, 4, 6],  // near=5, far=2
    [3, 7, 5, 4, 0, 2],  // near=6, far=1
    [2, 3, 1, 5, 4, 6],  // near=7, far=0
  ];

  static computeIsometricHull(cuboid, cosAngle, sinAngle, yScale, cubeSize) {
    // O(1) silhouette hull — no sorting, no atan2, no Graham scan.
    // Uses pre-computed winding orders for each camera octant.
    const x0 = cuboid.minX * cubeSize;
    const x1 = cuboid.maxX * cubeSize;
    // Use visual Y bounds for hull when available (sprites have larger visual extent
    // than their sorting AABB). This ensures screen overlap detection catches tall sprites
    // that visually overlap with walls even though their sorting AABB is shorter.
    const y0 = (cuboid.visualMinY !== undefined ? cuboid.visualMinY : cuboid.minY) * cubeSize;
    const y1 = (cuboid.visualMaxY !== undefined ? cuboid.visualMaxY : cuboid.maxY) * cubeSize;
    const z0 = -cuboid.maxZ * cubeSize;
    const z1 = -cuboid.minZ * cubeSize;

    // Degenerate case: flat cuboid where near and far corner coincide on an axis
    if (x0 === x1 || y0 === y1 || z0 === z1) {
      const corners = [
        { x: x0, y: y0, z: z0 }, { x: x1, y: y0, z: z0 },
        { x: x0, y: y1, z: z0 }, { x: x1, y: y1, z: z0 },
        { x: x0, y: y0, z: z1 }, { x: x1, y: y0, z: z1 },
        { x: x0, y: y1, z: z1 }, { x: x1, y: y1, z: z1 }
      ];
      const projected = corners.map(c => ({
        x: c.x * cosAngle - c.z * sinAngle,
        y: (c.x * sinAngle + c.z * cosAngle) * yScale - c.y
      }));
      return this.grahamScan(projected);
    }

    // Near corner index determines which octant winding table to use
    const nearIdx = (sinAngle >= 0 ? 1 : 0) + (yScale >= 0 ? 2 : 0) + (cosAngle >= 0 ? 4 : 0);
    const winding = this._HULL_WINDING[nearIdx];

    // All 8 corner world coordinates as flat arrays for direct indexing
    // Index bits: x_bit(+1) + y_bit(+2) + z_bit(+4)
    const wx = [x0, x1, x0, x1, x0, x1, x0, x1];
    const wy = [y0, y0, y1, y1, y0, y0, y1, y1];
    const wz = [z0, z0, z0, z0, z1, z1, z1, z1];

    // Emit 6 hull points in pre-computed CCW order — no sorting needed
    const hull = new Array(6);
    for (let i = 0; i < 6; i++) {
      const ci = winding[i];
      hull[i] = {
        x: wx[ci] * cosAngle - wz[ci] * sinAngle,
        y: (wx[ci] * sinAngle + wz[ci] * cosAngle) * yScale - wy[ci]
      };
    }

    return hull;
  }

  /**
   * Check if axis separates two polygons (helper for SAT)
   * @param {Object} axis - Axis vector {x, y}
   * @param {Array} hullA - First polygon vertices
   * @param {Array} hullB - Second polygon vertices
   * @return {boolean} True if axis separates the polygons
   */
  static isSeparatingAxis(axis, hullA, hullB) {
    // Normalize axis for consistent epsilon comparison
    const len = Math.sqrt(axis.x * axis.x + axis.y * axis.y);
    if (len < 1e-10) return false; // Degenerate axis, can't separate
    const nx = axis.x / len;
    const ny = axis.y / len;

    let minA = Infinity, maxA = -Infinity;
    let minB = Infinity, maxB = -Infinity;

    for (const p of hullA) {
      const proj = p.x * nx + p.y * ny;
      minA = Math.min(minA, proj);
      maxA = Math.max(maxA, proj);
    }

    for (const p of hullB) {
      const proj = p.x * nx + p.y * ny;
      minB = Math.min(minB, proj);
      maxB = Math.max(maxB, proj);
    }

    // Check for separation - use larger epsilon appropriate for screen-space coords
    // Touching faces (gap ~0) should be considered overlapping
    const EPSILON = 1.0;
    return maxA < minB - EPSILON || maxB < minA - EPSILON;
  }

  /**
   * Test if two convex polygons overlap using SAT
   * @param {Array} hullA - First polygon vertices in order
   * @param {Array} hullB - Second polygon vertices in order
   * @return {boolean} True if polygons overlap
   */
  static convexPolygonsOverlap(hullA, hullB) {
    // Handle degenerate hulls - if either hull is degenerate,
    // assume overlap (safer to check ordering than skip it)
    if (!hullA || !hullB || hullA.length < 3 || hullB.length < 3) {
      return true; // Assume overlap when uncertain
    }

    // Test all edges of polygon A as separating axes
    for (let i = 0; i < hullA.length; i++) {
      const j = (i + 1) % hullA.length;
      const edge = { x: hullA[j].x - hullA[i].x, y: hullA[j].y - hullA[i].y };
      const axis = { x: -edge.y, y: edge.x }; // Perpendicular

      if (this.isSeparatingAxis(axis, hullA, hullB)) return false;
    }

    // Test all edges of polygon B as separating axes
    for (let i = 0; i < hullB.length; i++) {
      const j = (i + 1) % hullB.length;
      const edge = { x: hullB[j].x - hullB[i].x, y: hullB[j].y - hullB[i].y };
      const axis = { x: -edge.y, y: edge.x };

      if (this.isSeparatingAxis(axis, hullA, hullB)) return false;
    }

    return true; // No separating axis found, polygons overlap
  }

  /**
   * Compute the XZ footprint (ground plane projection) in screen space
   * Projects the 4 bottom corners of the cuboid to screen space
   * @param {Object} cuboid - Cuboid with minX/maxX/minY/minZ/maxZ
   * @param {number} cosAngle - Cosine of camera rotation
   * @param {number} sinAngle - Sine of camera rotation
   * @param {number} yScale - Y scale factor from skew
   * @param {number} cubeSize - Size of one cube unit
   * @return {Array} Array of 4 {x, y} points forming the footprint quadrilateral
   */
  static computeXZFootprint(cuboid, cosAngle, sinAngle, yScale, cubeSize) {
    const baseY = cuboid.minY; // Use minY as the base

    // The 4 corners of the XZ footprint (bottom face)
    const corners = [
      { x: cuboid.minX, z: cuboid.minZ },
      { x: cuboid.maxX, z: cuboid.minZ },
      { x: cuboid.maxX, z: cuboid.maxZ },
      { x: cuboid.minX, z: cuboid.maxZ }
    ];

    return corners.map(corner => {
      const worldX = corner.x * cubeSize;
      const worldZ = -corner.z * cubeSize;
      const worldY = baseY * cubeSize;

      const screenX = worldX * cosAngle - worldZ * sinAngle;
      const screenY = (worldX * sinAngle + worldZ * cosAngle) * yScale - worldY;

      return { x: screenX, y: screenY };
    });
  }

  /**
   * Calculate the optimal split plane to break a depth cycle between two cuboids.
   *
   * A depth cycle occurs when cuboid A is in front of B in one region but behind B
   * in another region. We need to split along the axis where this depth relationship
   * changes - typically X or Z for isometric views.
   *
   * @param {Object} cuboidA - First cuboid (needs minX/maxX/minY/maxY/minZ/maxZ)
   * @param {Object} cuboidB - Second cuboid
   * @param {Object} camera - Camera with angle property
   * @return {Object|null} Split info {axis, position, cuboidToSplit} or null
   */
  static calculateSplitPlane(cuboidA, cuboidB, camera, debug = false) {
    // For depth cycles, we need to split on X or Z (not Y) because depth ordering
    // is determined by X-Z position in isometric projection.
    // Y only affects ordering when there's XZ overlap (one above the other).

    // Check if one cuboid "spans across" the other on X or Z
    // This means it extends beyond the other on both sides, creating a depth wrap
    const candidates = [];

    // Check X axis: does A span across B, or B span across A?
    // Use <= for boundary conditions to catch edge-sharing cases
    if (cuboidA.minX < cuboidB.minX && cuboidA.maxX > cuboidB.minX && cuboidA.maxX <= cuboidB.maxX) {
      candidates.push({ axis: 'x', position: cuboidB.minX, cuboidToSplit: 'A', quality: 1 });
    }
    if (cuboidA.maxX > cuboidB.maxX && cuboidA.minX < cuboidB.maxX && cuboidA.minX >= cuboidB.minX) {
      candidates.push({ axis: 'x', position: cuboidB.maxX, cuboidToSplit: 'A', quality: 1 });
    }
    if (cuboidB.minX < cuboidA.minX && cuboidB.maxX > cuboidA.minX && cuboidB.maxX <= cuboidA.maxX) {
      candidates.push({ axis: 'x', position: cuboidA.minX, cuboidToSplit: 'B', quality: 1 });
    }
    if (cuboidB.maxX > cuboidA.maxX && cuboidB.minX < cuboidA.maxX && cuboidB.minX >= cuboidA.minX) {
      candidates.push({ axis: 'x', position: cuboidA.maxX, cuboidToSplit: 'B', quality: 1 });
    }

    // Check Z axis: does A span across B, or B span across A?
    if (cuboidA.minZ < cuboidB.minZ && cuboidA.maxZ > cuboidB.minZ && cuboidA.maxZ <= cuboidB.maxZ) {
      candidates.push({ axis: 'z', position: cuboidB.minZ, cuboidToSplit: 'A', quality: 1 });
    }
    if (cuboidA.maxZ > cuboidB.maxZ && cuboidA.minZ < cuboidB.maxZ && cuboidA.minZ >= cuboidB.minZ) {
      candidates.push({ axis: 'z', position: cuboidB.maxZ, cuboidToSplit: 'A', quality: 1 });
    }
    if (cuboidB.minZ < cuboidA.minZ && cuboidB.maxZ > cuboidA.minZ && cuboidB.maxZ <= cuboidA.maxZ) {
      candidates.push({ axis: 'z', position: cuboidA.minZ, cuboidToSplit: 'B', quality: 1 });
    }
    if (cuboidB.maxZ > cuboidA.maxZ && cuboidB.minZ < cuboidA.maxZ && cuboidB.minZ >= cuboidA.minZ) {
      candidates.push({ axis: 'z', position: cuboidA.maxZ, cuboidToSplit: 'B', quality: 1 });
    }

    // If we found boundary-based splits, use the best one
    if (candidates.length > 0) {
      return candidates[0];
    }

    // Check for COMPLETE CONTAINMENT cases (one cuboid completely contains the other on an axis)
    // In these cases, split at the contained cuboid's boundary to create one fragment with no overlap
    // This is more effective than midpoint splitting for breaking cycles

    // X-axis: A completely contains B
    if (cuboidA.minX < cuboidB.minX && cuboidA.maxX > cuboidB.maxX) {
      candidates.push({ axis: 'x', position: cuboidB.minX, cuboidToSplit: 'A', quality: 2 });
    }
    // X-axis: B completely contains A
    if (cuboidB.minX < cuboidA.minX && cuboidB.maxX > cuboidA.maxX) {
      candidates.push({ axis: 'x', position: cuboidA.minX, cuboidToSplit: 'B', quality: 2 });
    }
    // Z-axis: A completely contains B
    if (cuboidA.minZ < cuboidB.minZ && cuboidA.maxZ > cuboidB.maxZ) {
      candidates.push({ axis: 'z', position: cuboidB.minZ, cuboidToSplit: 'A', quality: 2 });
    }
    // Z-axis: B completely contains A
    if (cuboidB.minZ < cuboidA.minZ && cuboidB.maxZ > cuboidA.maxZ) {
      candidates.push({ axis: 'z', position: cuboidA.minZ, cuboidToSplit: 'B', quality: 2 });
    }
    // Y-axis: A completely contains B
    if (cuboidA.minY < cuboidB.minY && cuboidA.maxY > cuboidB.maxY) {
      candidates.push({ axis: 'y', position: cuboidB.minY, cuboidToSplit: 'A', quality: 2 });
    }
    // Y-axis: B completely contains A
    if (cuboidB.minY < cuboidA.minY && cuboidB.maxY > cuboidA.maxY) {
      candidates.push({ axis: 'y', position: cuboidA.minY, cuboidToSplit: 'B', quality: 2 });
    }

    if (candidates.length > 0) {
      // Prefer X and Z over Y for containment splits too
      const xzCandidates = candidates.filter(c => c.axis !== 'y');
      return xzCandidates.length > 0 ? xzCandidates[0] : candidates[0];
    }

    // Fallback: check for any overlap and split in the middle
    // IMPORTANT: Don't split on axes where ranges are identical - midpoint split won't break the cycle!
    const getOverlap = (min1, max1, min2, max2) => {
      const overlapMin = Math.max(min1, min2);
      const overlapMax = Math.min(max1, max2);
      return overlapMin < overlapMax ? { min: overlapMin, max: overlapMax, size: overlapMax - overlapMin } : null;
    };

    // Check if ranges are essentially identical (midpoint split won't help)
    const RANGE_EPSILON = 0.01;
    const xRangesIdentical = Math.abs(cuboidA.minX - cuboidB.minX) < RANGE_EPSILON &&
                             Math.abs(cuboidA.maxX - cuboidB.maxX) < RANGE_EPSILON;
    const zRangesIdentical = Math.abs(cuboidA.minZ - cuboidB.minZ) < RANGE_EPSILON &&
                             Math.abs(cuboidA.maxZ - cuboidB.maxZ) < RANGE_EPSILON;

    // Try Y boundary splits for Y-stacked cuboids (when X and Z ranges are identical)
    if (xRangesIdentical && zRangesIdentical) {
      // Cuboids are vertically stacked - try Y boundary splits
      if (cuboidA.minY < cuboidB.minY && cuboidA.maxY > cuboidB.minY && cuboidA.maxY <= cuboidB.maxY) {
        return { axis: 'y', position: cuboidB.minY, cuboidToSplit: 'A', quality: 1 };
      }
      if (cuboidA.maxY > cuboidB.maxY && cuboidA.minY < cuboidB.maxY && cuboidA.minY >= cuboidB.minY) {
        return { axis: 'y', position: cuboidB.maxY, cuboidToSplit: 'A', quality: 1 };
      }
      if (cuboidB.minY < cuboidA.minY && cuboidB.maxY > cuboidA.minY && cuboidB.maxY <= cuboidA.maxY) {
        return { axis: 'y', position: cuboidA.minY, cuboidToSplit: 'B', quality: 1 };
      }
      if (cuboidB.maxY > cuboidA.maxY && cuboidB.minY < cuboidA.maxY && cuboidB.minY >= cuboidA.minY) {
        return { axis: 'y', position: cuboidA.maxY, cuboidToSplit: 'B', quality: 1 };
      }

      // If Y ranges are also identical or no boundary split possible, give up
      return null;
    }

    // NO MIDPOINT FALLBACK!
    // If we couldn't find a boundary intersection (spanning or containment),
    // midpoint splits are almost always useless for resolving depth cycles.
    // They create fragments with the same depth ambiguity as the originals.
    // Better to give up and use depth-based ordering than to explode fragment count.
    return null;
  }

  /**
   * Choose which cuboid to split based on where the split would be more balanced.
   *
   * @param {Object} cuboidA - First cuboid
   * @param {Object} cuboidB - Second cuboid
   * @param {string} axis - Split axis ('x', 'y', or 'z')
   * @param {number} position - Split position
   * @return {string} 'A' or 'B' indicating which cuboid to split
   */
  static chooseCuboidToSplit(cuboidA, cuboidB, axis, position) {
    // Get the dimension along this axis for each cuboid
    const getSize = (cuboid, ax) => {
      const minKey = 'min' + ax.toUpperCase();
      const maxKey = 'max' + ax.toUpperCase();
      return cuboid[maxKey] - cuboid[minKey];
    };

    const getMin = (cuboid, ax) => cuboid['min' + ax.toUpperCase()];
    const getMax = (cuboid, ax) => cuboid['max' + ax.toUpperCase()];

    const sizeA = getSize(cuboidA, axis);
    const sizeB = getSize(cuboidB, axis);

    // Check if position is inside each cuboid
    const insideA = position > getMin(cuboidA, axis) && position < getMax(cuboidA, axis);
    const insideB = position > getMin(cuboidB, axis) && position < getMax(cuboidB, axis);

    if (insideA && !insideB) return 'A';
    if (insideB && !insideA) return 'B';
    if (!insideA && !insideB) return null; // Position is outside both - shouldn't happen

    // Both cuboids contain the position - choose based on balance
    // Calculate how centered the split would be for each
    const centerA = (getMin(cuboidA, axis) + getMax(cuboidA, axis)) / 2;
    const centerB = (getMin(cuboidB, axis) + getMax(cuboidB, axis)) / 2;

    const distFromCenterA = Math.abs(position - centerA) / sizeA;
    const distFromCenterB = Math.abs(position - centerB) / sizeB;

    // Prefer splitting the cuboid where the cut is more centered
    // Also factor in size - prefer splitting larger cuboids
    const scoreA = distFromCenterA - sizeA * 0.01;
    const scoreB = distFromCenterB - sizeB * 0.01;

    return scoreA <= scoreB ? 'A' : 'B';
  }
}
